import { useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { HOME_PIPELINES } from '../../lib/stages';
import { SegmentedControl } from '../../ui/kit/components/navigation';
import { Badge, IconButton } from '../../ui/kit/components/primitives';
import { Icon } from '../../ui/kit/components/icons';
import { PIPELINE_ICONS } from './ProjectTile';
import PipelineScene from './PipelineScene';

export default function ProductionPipeline() {
  const id = useId();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const [kind, setKind] = useState(HOME_PIPELINES[0].id);
  const [index, setIndex] = useState(0);
  const pipeline = HOME_PIPELINES.find(item => item.id === kind) || HOME_PIPELINES[0];
  const stages = pipeline.stages;
  const stage = stages[Math.min(index, stages.length - 1)];
  const single = stages.length === 1;

  const select = (next: number, focus = false) => {
    const bounded = (next + stages.length) % stages.length;
    setIndex(bounded);
    if (focus) tabs.current[bounded]?.focus();
  };

  const onKey = (event: KeyboardEvent, current: number) => {
    const moves: Record<string, number> = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: stages.length - 1 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    select(moves[event.key], true);
  };

  return <section className="studio-pipeline studio-home-reveal" aria-labelledby={`${id}-title`} style={{ animationDelay: '200ms' }}>
    <div className="studio-pipeline-head">
      <div>
        <p className="bs-eyebrow">How the pipeline works</p>
        <h2 id={`${id}-title`}>From brief to delivery</h2>
      </div>
      <SegmentedControl label="Production type" value={kind} onChange={value => { setKind(value); setIndex(0); }} items={HOME_PIPELINES.map(item => ({ value: item.id, label: item.title, icon: PIPELINE_ICONS[item.id] }))} />
    </div>

    {!single && <div className="studio-pipeline-scroller"><div className="studio-pipeline-track" style={{ '--steps': stages.length, '--at': index } as CSSProperties}>
      <span className="studio-pipeline-rail" aria-hidden="true"><i /></span>
      <div role="tablist" aria-label={`${pipeline.title} stages`} className="studio-pipeline-steps">
        {stages.map((item, i) => <button key={`${kind}-${item.id}`} ref={el => { tabs.current[i] = el; }} type="button" role="tab" id={`${id}-tab-${i}`} aria-selected={i === index} aria-controls={`${id}-panel`} tabIndex={i === index ? 0 : -1} className={i < index ? 'is-done' : undefined} onClick={() => select(i)} onKeyDown={event => onKey(event, i)}>
          <span className="studio-pipeline-node bs-mono">{i < index ? <Icon name="check" size={13} /> : item.number}</span>
          <span className="studio-pipeline-name">{item.plain}</span>
        </button>)}
      </div>
    </div></div>}

    <div id={`${id}-panel`} role={single ? undefined : 'tabpanel'} aria-labelledby={single ? undefined : `${id}-tab-${index}`} className="studio-pipeline-detail">
      <div className="studio-pipeline-info">
        <div key={`${kind}-${stage.id}`} className="studio-pipeline-copy">
          <p className="studio-pipeline-count bs-mono">Stage {stage.number} of {String(stages.length).padStart(2, '0')}</p>
          <h3>{stage.title}</h3>
          <p className="bs-muted">{stage.sub}</p>
          <Badge icon="sparkle">Led by {stage.agent}</Badge>
        </div>
        {!single && <div className="studio-pipeline-nav">
          <IconButton icon="back" label="Previous stage" size="sm" disabled={index === 0} onClick={() => select(index - 1)} />
          <IconButton icon="arrow" label="Next stage" size="sm" disabled={index === stages.length - 1} onClick={() => select(index + 1)} />
        </div>}
      </div>
      <PipelineScene key={`${kind}-${stage.id}`} kind={kind} stage={stage.id} />
    </div>
  </section>;
}
